import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged, signOut, signInWithEmailAndPassword, createUserWithEmailAndPassword, updateProfile, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { auth } from '../firebase';
import { saveClientToDatabase, checkClientExists, hasAcceptedTerms } from '../supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [termsAccepted, setTermsAccepted] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
      
      // Si hay un usuario autenticado, verificar y guardar en Supabase en segundo plano
      // Hacer esto después de actualizar el estado para no bloquear la navegación
      if (user && user.email) {
        // Ejecutar en segundo plano sin bloquear
        (async () => {
          try {
            const existingClient = await checkClientExists(user.email);
            
            if (!existingClient) {
              await saveClientToDatabase({
                email: user.email,
                displayName: user.displayName,
                name: user.displayName,
                uid: user.uid
              });
              // Usuario nuevo, no ha aceptado términos
              setTermsAccepted(false);
            } else {
              // Verificar si ha aceptado los términos
              const accepted = await hasAcceptedTerms(user.email);
              setTermsAccepted(accepted);
            }
          } catch (error) {
            setTermsAccepted(false);
          }
        })();
      } else {
        setTermsAccepted(null);
      }
    });

    return unsubscribe;
  }, []);

  const login = async (email, password) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      throw error;
    }
  };

  const register = async (email, password, name) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(userCredential.user, {
        displayName: name
      });
      
      // Guardar usuario en Supabase después de crear el usuario en Firebase
      try {
        await saveClientToDatabase({
          email: userCredential.user.email,
          displayName: name,
          name: name,
          uid: userCredential.user.uid
        });
      } catch (error) {
        // No lanzar error para que el registro de Firebase no falle
      }
    } catch (error) {
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      // Verificar y guardar usuario en Supabase inmediatamente
      if (result.user && result.user.email) {
        try {
          const existingClient = await checkClientExists(result.user.email);
          
          if (!existingClient) {
            // Usuario nuevo, guardar en Supabase
            await saveClientToDatabase({
              email: result.user.email,
              displayName: result.user.displayName,
              name: result.user.displayName,
              uid: result.user.uid
            });
            // Usuario nuevo, no ha aceptado términos
            setTermsAccepted(false);
            return { needsTermsAcceptance: true };
          } else {
            // Usuario existente, verificar si ha aceptado términos
            const accepted = await hasAcceptedTerms(result.user.email);
            setTermsAccepted(accepted);
            return { needsTermsAcceptance: !accepted };
          }
        } catch (error) {
          setTermsAccepted(false);
          return { needsTermsAcceptance: true };
        }
      }
      
      return { needsTermsAcceptance: false };
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
    } catch (error) {
    }
  };

  const value = {
    user,
    loading,
    termsAccepted,
    setTermsAccepted,
    login,
    register,
    signInWithGoogle,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
